import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSessao } from '../auth/SessaoContext';
import { api, ErroApi } from '../api/cliente';
import type {
  DadosCadastro,
  DadosCnpj,
  EnderecoCep,
  TipoPessoa,
} from '../api/tipos';
import { Aviso, Botao, Campo, Girador, Selecao, cx } from '../componentes/ui';
import { CabecalhoAuth, MolduraAuth } from './MolduraAuth';
import css from './Auth.module.css';
import {
  documentoValido,
  mascaraCep,
  mascaraDocumento,
  mascaraTelefone,
  soDigitos,
  telefoneValido,
  UFS,
} from '../util/mascaras';

/** Espelha as regras do RegistrarDto — a validação que vale é a do servidor. */
function validarSenha(senha: string): string | undefined {
  if (senha.length < 10) return 'Use ao menos 10 caracteres.';
  if (!/[A-Za-z]/.test(senha)) return 'Inclua ao menos uma letra.';
  if (!/\d/.test(senha)) return 'Inclua ao menos um número.';
  return undefined;
}

const VAZIO = {
  tipoPessoa: 'JURIDICA' as TipoPessoa,
  documento: '',
  nome: '',
  email: '',
  senha: '',
  telefone: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

type Campos = typeof VAZIO;

/**
 * Cadastro em dois passos.
 *
 * Um formulário único com 12 campos derruba a conversão e contradiz o "leva
 * menos de um minuto" da tela. Passo 1 é identidade e acesso; passo 2 é o
 * endereço, quase todo preenchido pelo CEP.
 */
export function Cadastro() {
  const { cadastrar } = useSessao();
  const navegar = useNavigate();

  const [passo, setPasso] = useState<1 | 2>(1);
  const [c, setC] = useState<Campos>(VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const pj = c.tipoPessoa === 'JURIDICA';
  const set = <K extends keyof Campos>(campo: K, valor: Campos[K]) =>
    setC((atual) => ({ ...atual, [campo]: valor }));

  return (
    <MolduraAuth>
      <div className={css.pilha}>
        <CabecalhoAuth titulo="Criar sua conta">
          Passo {passo} de 2 — {passo === 1 ? 'seus dados' : 'endereço'}
        </CabecalhoAuth>

        <Trilha passo={passo} />

        {erro && <Aviso>{erro}</Aviso>}

        {passo === 1 ? (
          <PassoIdentidade
            c={c}
            set={set}
            pj={pj}
            setErro={setErro}
            aoAvancar={() => {
              setErro(null);
              setPasso(2);
            }}
          />
        ) : (
          <PassoEndereco
            c={c}
            set={set}
            enviando={enviando}
            aoVoltar={() => {
              setErro(null);
              setPasso(1);
            }}
            aoEnviar={async () => {
              setErro(null);
              setEnviando(true);
              try {
                const dados: DadosCadastro = {
                  tipoPessoa: c.tipoPessoa,
                  documento: soDigitos(c.documento),
                  nome: c.nome.trim(),
                  email: c.email.trim(),
                  senha: c.senha,
                  cep: soDigitos(c.cep),
                  logradouro: c.logradouro.trim(),
                  numero: c.numero.trim(),
                  bairro: c.bairro.trim(),
                  cidade: c.cidade.trim(),
                  uf: c.uf,
                };
                if (c.telefone) dados.telefone = soDigitos(c.telefone);
                if (c.complemento) dados.complemento = c.complemento.trim();

                await cadastrar(dados);
                navegar('/verificar-email', { replace: true });
              } catch (err) {
                setErro(
                  err instanceof ErroApi
                    ? err.message
                    : 'Não foi possível criar a conta. Verifique sua conexão.',
                );
                // Erro de e-mail/documento duplicado é do passo 1 — voltar para
                // lá evita o usuário procurar o problema na tela errada.
                if (err instanceof ErroApi && err.status === 409) setPasso(1);
              } finally {
                setEnviando(false);
              }
            }}
          />
        )}

        <span className={css.alternativa}>
          Já tem conta? <Link to="/entrar">Entrar</Link>
        </span>
      </div>
    </MolduraAuth>
  );
}

function Trilha({ passo }: { passo: 1 | 2 }) {
  return (
    <div className={css.trilha} aria-hidden="true">
      {(['Seus dados', 'Endereço'] as const).map((rotulo, i) => (
        <span
          key={rotulo}
          className={cx(css.trilhaPasso, i + 1 <= passo && css.trilhaAtivo)}
        >
          {rotulo}
        </span>
      ))}
    </div>
  );
}

/* ── Passo 1: identidade e acesso ───────────────────────────────────────── */

function PassoIdentidade({
  c,
  set,
  pj,
  setErro,
  aoAvancar,
}: {
  c: Campos;
  set: <K extends keyof Campos>(campo: K, valor: Campos[K]) => void;
  pj: boolean;
  setErro: (m: string | null) => void;
  aoAvancar: () => void;
}) {
  const [tocou, setTocou] = useState<Record<string, boolean>>({});
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  const erroDoc =
    tocou.documento && c.documento && !documentoValido(c.documento, pj)
      ? `${pj ? 'CNPJ' : 'CPF'} inválido.`
      : undefined;
  const erroSenha = tocou.senha ? validarSenha(c.senha) : undefined;
  const erroTel =
    tocou.telefone && c.telefone && !telefoneValido(c.telefone)
      ? 'Telefone inválido.'
      : undefined;

  /**
   * Puxa razão social e endereço da Receita quando o CNPJ fica completo.
   *
   * Falha aqui NUNCA vira erro de formulário: a consulta é conveniência, e o
   * provedor externo pode estar fora do ar ou limitando (429). Bloquear o
   * cadastro por causa disso seria trocar um serviço opcional por um requisito.
   */
  async function buscarCnpj() {
    if (!pj || !documentoValido(c.documento, true)) return;
    setBuscandoCnpj(true);
    try {
      const d = await api<DadosCnpj>(
        `/cadastro/cnpj/${soDigitos(c.documento)}`,
      );
      set('nome', d.razaoSocial);
      if (d.endereco.cep) {
        set('cep', mascaraCep(d.endereco.cep));
        set('logradouro', d.endereco.logradouro);
        set('numero', d.endereco.numero);
        set('complemento', d.endereco.complemento ?? '');
        set('bairro', d.endereco.bairro);
        set('cidade', d.endereco.cidade);
        set('uf', d.endereco.uf);
      }
    } catch {
      // Consulta é conveniência: indisponível, a pessoa digita. Não vira erro
      // de formulário para não parecer que o cadastro travou.
    } finally {
      setBuscandoCnpj(false);
    }
  }

  function avancar(e: FormEvent) {
    e.preventDefault();
    setTocou({
      documento: true,
      nome: true,
      email: true,
      senha: true,
      telefone: true,
    });

    if (!documentoValido(c.documento, pj)) {
      setErro(`Informe um ${pj ? 'CNPJ' : 'CPF'} válido.`);
      return;
    }
    if (c.nome.trim().length < 2) {
      setErro(pj ? 'Informe a razão social.' : 'Informe seu nome completo.');
      return;
    }
    if (validarSenha(c.senha)) return;
    if (c.telefone && !telefoneValido(c.telefone)) return;

    aoAvancar();
  }

  return (
    <form onSubmit={avancar} noValidate className={css.campos}>
      <fieldset className={css.tipos}>
        <legend>Você é</legend>
        <div className={css.segmentos}>
          <OpcaoTipo
            ativo={pj}
            onClick={() => {
              set('tipoPessoa', 'JURIDICA');
              // O documento anterior é de outro tipo; manter confundiria a
              // validação e a máscara.
              set('documento', '');
            }}
          >
            Empresa (CNPJ)
          </OpcaoTipo>
          <OpcaoTipo
            ativo={!pj}
            onClick={() => {
              set('tipoPessoa', 'FISICA');
              set('documento', '');
            }}
          >
            Pessoa física (CPF)
          </OpcaoTipo>
        </div>
      </fieldset>

      <Campo
        rotulo={pj ? 'CNPJ' : 'CPF'}
        inputMode="numeric"
        autoComplete="off"
        required
        value={c.documento}
        onChange={(e) => set('documento', mascaraDocumento(e.target.value, pj))}
        onBlur={() => {
          setTocou((t) => ({ ...t, documento: true }));
          void buscarCnpj();
        }}
        erro={erroDoc}
        dica={
          pj && !erroDoc
            ? 'Ao sair do campo, buscamos razão social e endereço na base da Receita.'
            : undefined
        }
        placeholder={pj ? '00.000.000/0000-00' : '000.000.000-00'}
        acessorio={buscandoCnpj ? <Girador tamanho={13} /> : undefined}
      />

      <Campo
        rotulo={pj ? 'Razão social' : 'Nome completo'}
        autoComplete={pj ? 'organization' : 'name'}
        required
        minLength={2}
        maxLength={120}
        value={c.nome}
        onChange={(e) => set('nome', e.target.value)}
        placeholder={
          pj ? 'Contabilidade Nova Era LTDA' : 'Como devemos te chamar'
        }
      />

      <Campo
        rotulo="E-mail profissional"
        type="email"
        autoComplete="email"
        required
        value={c.email}
        onChange={(e) => set('email', e.target.value)}
        dica="Vamos enviar um código de 6 dígitos para confirmar este endereço."
        placeholder="voce@empresa.com.br"
      />

      <Campo
        rotulo="WhatsApp / telefone (opcional)"
        inputMode="tel"
        autoComplete="tel"
        value={c.telefone}
        onChange={(e) => set('telefone', mascaraTelefone(e.target.value))}
        onBlur={() => setTocou((t) => ({ ...t, telefone: true }))}
        erro={erroTel}
        placeholder="(11) 90000-0000"
      />

      <Campo
        rotulo="Senha"
        type="password"
        autoComplete="new-password"
        required
        value={c.senha}
        onChange={(e) => set('senha', e.target.value)}
        onBlur={() => setTocou((t) => ({ ...t, senha: true }))}
        erro={erroSenha}
        dica="Mínimo de 10 caracteres, com uma letra e um número."
      />

      <Botao type="submit" larguraTotal grande>
        Continuar
      </Botao>
    </form>
  );
}

function OpcaoTipo({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={css.segmento}
    >
      {children}
    </button>
  );
}

/* ── Passo 2: endereço ──────────────────────────────────────────────────── */

function PassoEndereco({
  c,
  set,
  enviando,
  aoVoltar,
  aoEnviar,
}: {
  c: Campos;
  set: <K extends keyof Campos>(campo: K, valor: Campos[K]) => void;
  enviando: boolean;
  aoVoltar: () => void;
  aoEnviar: () => void | Promise<void>;
}) {
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState<string | undefined>();

  /**
   * Busca o endereço pelo CEP através do NOSSO backend (`/api/cadastro/cep`),
   * não do ViaCEP direto: a CSP da página só permite requisições à própria
   * origem, e assim o IP do usuário não vai para um terceiro.
   */
  async function buscarCep(valor: string) {
    const cep = soDigitos(valor);
    setErroCep(undefined);
    if (cep.length !== 8) return;

    setBuscandoCep(true);
    try {
      const e = await api<EnderecoCep>(`/cadastro/cep/${cep}`);
      set('logradouro', e.logradouro);
      set('bairro', e.bairro);
      set('cidade', e.cidade);
      set('uf', e.uf);
    } catch (err) {
      setErroCep(
        err instanceof ErroApi && err.status === 404
          ? 'CEP não encontrado. Preencha o endereço manualmente.'
          : 'Não foi possível consultar o CEP. Preencha manualmente.',
      );
    } finally {
      setBuscandoCep(false);
    }
  }

  const completo =
    soDigitos(c.cep).length === 8 &&
    c.logradouro.trim().length >= 2 &&
    c.numero.trim().length >= 1 &&
    c.bairro.trim().length >= 2 &&
    c.cidade.trim().length >= 2 &&
    c.uf.length === 2;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void aoEnviar();
      }}
      noValidate
      className={css.campos}
    >
      <Campo
        rotulo="CEP"
        inputMode="numeric"
        autoComplete="postal-code"
        required
        autoFocus
        value={c.cep}
        onChange={(e) => {
          const v = mascaraCep(e.target.value);
          set('cep', v);
          if (soDigitos(v).length === 8) void buscarCep(v);
        }}
        erro={erroCep}
        dica={
          erroCep ? undefined : 'Preenchemos o resto do endereço para você.'
        }
        placeholder="00000-000"
        acessorio={buscandoCep ? <Girador tamanho={13} /> : undefined}
      />

      <Campo
        rotulo="Logradouro"
        autoComplete="address-line1"
        required
        value={c.logradouro}
        onChange={(e) => set('logradouro', e.target.value)}
        placeholder="Rua, avenida…"
      />

      <div className={css.linha}>
        <Campo
          rotulo="Número"
          required
          value={c.numero}
          onChange={(e) => set('numero', e.target.value)}
          placeholder="123 ou S/N"
        />
        <Campo
          rotulo="Complemento (opcional)"
          autoComplete="address-line2"
          maxLength={80}
          value={c.complemento}
          onChange={(e) => set('complemento', e.target.value)}
          placeholder="Sala, andar…"
        />
      </div>

      <Campo
        rotulo="Bairro"
        required
        value={c.bairro}
        onChange={(e) => set('bairro', e.target.value)}
      />

      <div className={cx(css.linha, css.linhaCidade)}>
        <Campo
          rotulo="Cidade"
          autoComplete="address-level2"
          required
          value={c.cidade}
          onChange={(e) => set('cidade', e.target.value)}
        />
        <Selecao
          rotulo="UF"
          required
          value={c.uf}
          onChange={(e) => set('uf', e.target.value)}
        >
          <option value="">—</option>
          {UFS.map((uf) => (
            <option key={uf} value={uf}>
              {uf}
            </option>
          ))}
        </Selecao>
      </div>

      <div className={css.acoes}>
        <Botao
          aparencia="secundario"
          grande
          onClick={aoVoltar}
          disabled={enviando}
        >
          Voltar
        </Botao>
        <Botao
          type="submit"
          larguraTotal
          grande
          carregando={enviando}
          disabled={!completo}
        >
          Criar conta
        </Botao>
      </div>
    </form>
  );
}
